import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import axios from '../lib/axios';
import io from 'socket.io-client';
import { toast } from 'sonner';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

const SOCKET_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [incomingCall, setIncomingCall] = useState(null);
  const socketRef = useRef(null);

  useEffect(() => {
    if (!user) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      return;
    }

    const newSocket = io(SOCKET_URL, {
      transports: ['websocket', 'polling']
    });

    newSocket.on('connect', () => {
      if (user) {
        newSocket.emit('join_room', { room: user.id });
      }
    });

    newSocket.on('incoming_call', (data) => {
      playSound('call');
      setIncomingCall(data);
      toast.info(`${data.caller_name} is calling you!`, {
        description: 'Wake up boss!',
        duration: 10000
      });
    });

    newSocket.on('call_ended', () => {
      setIncomingCall(null);
    });

    newSocket.on('transaction_status_update', () => {
      playSound('success');
      toast.success('Transaction approved!', {
        description: 'Your transaction has been approved by admin'
      });
    });

    socketRef.current = newSocket;

    return () => {
      newSocket.disconnect();
      socketRef.current = null;
    };
  }, [user]);

  const playSound = (type) => {
    try {
      let audio;
      switch(type) {
        case 'call':
          audio = new Audio('/sounds/wake-up-boss.mp3');
          break;
        case 'success':
          audio = new Audio('/sounds/success.mp3');
          break;
        default:
          return;
      }
      audio.volume = 0.5;
      audio.play().catch(() => {});
    } catch (error) {
      // Silent fail
    }
  };

  const answerCall = async (callId) => {
    try {
      await axios.post(`/calls/${callId}/answer`);
      setIncomingCall(null);
      return true;
    } catch (error) {
      toast.error('Failed to answer call');
      return false;
    }
  };

  const rejectCall = async (callId) => {
    try {
      await axios.post(`/calls/${callId}/end`);
      setIncomingCall(null);
      return true;
    } catch (error) {
      return false;
    }
  };

  return (
    <NotificationContext.Provider 
      value={{ 
        incomingCall,
        answerCall,
        rejectCall,
        playSound
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
